"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactDOM from "react-dom";

import EditableRequestCard from "./EditableRequestCard";
import axios from "axios";
import { format, parseISO } from "date-fns";
import TagChips, {
  DuplicateBadge,
  matchesTextQuery,
  buildTagOptions,
  hasAllTags,
} from "./Pieceworktags";

const API = "https://erp-xe9u.onrender.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });

// Add near the top alongside fmt/fmtQty:
const fmtRate = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
const cls = (...c) => c.filter(Boolean).join(" ");

const requiresDimensions = (uom) => {
  const u = (uom || "").toUpperCase();
  return u.includes("SF") || u.includes("LF");
};

const getDimMode = (uom) => {
  const u = (uom || "").toUpperCase();
  if (u.includes("SF")) return "sf";
  if (u.includes("LF")) return "lf";
  return "qty";
};

const AREA_TYPES = [
  "Units",
  "Corridor",
  "Shaft Walls",
  "Bulkheads",
  "Columns",
  "Common Area",
  "Other",
];

const STATUS_BADGE = {
  submitted: "bg-yellow-700/60 text-yellow-100",
  confirmed: "bg-green-700/60 text-green-200",
  rejected: "bg-red-800/60 text-red-200",
  draft: "bg-gray-700/60 text-gray-300",
};

const STATUS_BORDER = {
  submitted: "border-yellow-700/30",
  confirmed: "border-green-700/30",
  rejected: "border-red-700/30",
  draft: "border-gray-700",
};

const STATUS_TABS = ["all", "submitted", "confirmed", "rejected"];

// ─────────────────────────────────────────────────────────────────────────────
// REJECT MODAL
// ─────────────────────────────────────────────────────────────────────────────
function RejectModal({ open, onClose, onConfirm, submitting }) {
  const [reason, setReason] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 space-y-4">
        <h3 className="text-base font-semibold text-orange-400">
          Reject Request
        </h3>
        <p className="text-sm text-gray-400">
          Provide a reason so the worker can correct and resubmit.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Quantities don't match site report, wrong area selected…"
          rows={3}
          className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-red-500 focus:outline-none resize-none"
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={submitting || !reason.trim()}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors"
          >
            {submitting ? "Rejecting…" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LINE ITEMS TABLE
// ─────────────────────────────────────────────────────────────────────────────
function LineItemsTable({ items, hstRate }) {
  let totalSub = 0;
  const laborRows = (items || []).filter(
    (item) => item.type !== "material" && !item.poItemCode && !!item.itemCode,
  );

  const rows = laborRows.map((item) => {
    const uom = (item.uom || "").toUpperCase();
    const isLF = uom.includes("LF");
    const isSF = uom.includes("SF");
    const needsDims = isLF || isSF;
    const effectiveQty = needsDims
      ? item.computedCoverage || 0
      : item.quantity || 0;
    const sub = parseFloat((effectiveQty * (item.unitRate || 0)).toFixed(2));
    const hst = parseFloat(((sub * (hstRate || 0)) / 100).toFixed(2));
    totalSub += sub;
    return {
      ...item,
      effectiveQty,
      sub,
      hst,
      total: sub + hst,
      needsDims,
      isLF,
      isSF,
    };
  });

  const totalHst = parseFloat(((totalSub * (hstRate || 0)) / 100).toFixed(2));

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-700">
      <table className="w-full text-xs">
        <thead className="bg-gray-900">
          <tr>
            {[
              "Description",
              "UOM",
              "Coverage / Qty",
              "Unit Rate",
              "Subtotal",
              "HST",
              "Total",
            ].map((h) => (
              <th
                key={h}
                className="px-3 py-2.5 text-left text-gray-400 font-medium whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/50">
          {rows.map((row, idx) => (
            <tr
              key={idx}
              className={idx % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"}
            >
              <td className="px-3 py-2 text-gray-200 max-w-[200px]">
                <p className="font-medium truncate">{row.description || "—"}</p>
                <p className="text-[10px] font-mono text-orange-400 mt-0.5">
                  {row.itemCode}
                </p>
                {row.areaType && (
                  <p className="text-[9px] text-gray-500 mt-0.5">
                    {row.areaType}
                  </p>
                )}
              </td>
              <td className="px-3 py-2 text-gray-400 font-mono">
                {row.uom || "—"}
              </td>
              <td className="px-3 py-2 text-blue-300 font-mono font-semibold">
                {fmtQty(row.effectiveQty)}
                {row.needsDims &&
                  row.quantity > 0 &&
                  row.computedCoverage > 0 && (
                    <span className="block text-[9px] text-gray-500 font-normal">
                      {fmtQty(row.quantity)} pcs
                    </span>
                  )}
              </td>
              <td className="px-3 py-2 text-gray-300 font-mono">
                ${fmtRate(row.unitRate)}
              </td>
              <td className="px-3 py-2 text-gray-200 font-mono">
                ${fmt(row.sub)}
              </td>
              <td className="px-3 py-2 text-gray-400 font-mono">
                ${fmt(row.hst)}
              </td>
              <td className="px-3 py-2 text-orange-300 font-mono font-semibold">
                ${fmt(row.total)}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                No labor items.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot className="bg-gray-900 border-t-2 border-orange-400/30">
          <tr>
            <td
              colSpan={4}
              className="px-3 py-2.5 text-xs text-gray-400 font-semibold"
            >
              Totals
            </td>
            <td className="px-3 py-2.5 font-bold text-gray-200 font-mono">
              ${fmt(totalSub)}
            </td>
            <td className="px-3 py-2.5 font-bold text-gray-300 font-mono">
              ${fmt(totalHst)}
            </td>
            <td className="px-3 py-2.5 font-bold text-orange-300 font-mono text-sm">
              ${fmt(totalSub + totalHst)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PO SEARCH PANEL
// ─────────────────────────────────────────────────────────────────────────────
function POSearchPanel({ token, projectId, onSelect, alreadyAdded = [] }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(
          `${API}/purchaseOrders3/project/${projectId}/items`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { search: query, limit: 15 },
          },
        );
        const raw =
          res.data.items ||
          res.data.data ||
          (Array.isArray(res.data) ? res.data : []);
        setResults(raw.slice(0, 12));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, token, projectId]);

  const DELIVERY_BADGE = {
    not_ordered: { label: "Not ordered", cls: "bg-gray-700 text-gray-400" },
    ordered: { label: "Ordered", cls: "bg-blue-900/60 text-blue-300" },
    partial: { label: "Partial", cls: "bg-yellow-900/60 text-yellow-300" },
    delivered: { label: "Delivered", cls: "bg-green-900/60 text-green-300" },
  };
  const SOURCE_LABEL = {
    budget: null,
    change_order: { label: "CO", cls: "bg-purple-900/50 text-purple-300" },
    manual: { label: "Manual", cls: "bg-gray-700 text-gray-400" },
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus-within:border-amber-500 transition-colors">
        <svg
          className="w-3.5 h-3.5 text-amber-600 shrink-0"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clipRule="evenodd"
          />
        </svg>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search materials by code or description…"
          className="flex-1 text-sm bg-transparent text-gray-200 placeholder-gray-500 focus:outline-none"
        />
        {searching && (
          <div className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin shrink-0" />
        )}
        {query && !searching && (
          <button
            onClick={() => {
              setQuery("");
              setResults([]);
            }}
            className="text-gray-500 hover:text-gray-300 text-base leading-none"
          >
            ×
          </button>
        )}
      </div>
      {results.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-gray-800 border border-gray-600 rounded-xl shadow-2xl max-h-64 overflow-y-auto divide-y divide-gray-600/40">
          {results.map((item, i) => {
            const key = String(item._id);
            const already = alreadyAdded.includes(item.itemCode || key);
            const delivery =
              DELIVERY_BADGE[item.deliveryStatus] || DELIVERY_BADGE.not_ordered;
            const sourceTag = SOURCE_LABEL[item.source];
            return (
              <button
                key={i}
                disabled={already}
                onClick={() => {
                  if (!already) {
                    onSelect(item);
                    setQuery("");
                    setResults([]);
                  }
                }}
                className={cls(
                  "w-full text-left px-3 py-2.5 flex items-start justify-between gap-3 border-b border-gray-600/40 last:border-0 transition-colors",
                  already
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:bg-gray-600/40",
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-mono text-amber-400 shrink-0">
                      {item.itemCode || item.matCode || "—"}
                    </span>
                    {item.matCode && item.matCode !== item.itemCode && (
                      <span className="text-[9px] font-mono text-gray-500 shrink-0">
                        {item.matCode}
                      </span>
                    )}
                    {item.poNumber && (
                      <span className="text-[9px] text-gray-500 bg-gray-700 px-1.5 py-0.5 rounded shrink-0">
                        {item.poNumber}
                      </span>
                    )}
                    {sourceTag && (
                      <span
                        className={cls(
                          "text-[9px] px-1.5 py-0.5 rounded font-semibold shrink-0",
                          sourceTag.cls,
                        )}
                      >
                        {sourceTag.label}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-200 font-medium truncate mt-0.5">
                    {item.description || "—"}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {item.size && (
                      <span className="text-[10px] text-gray-500">
                        {item.size}
                      </span>
                    )}
                    {item.sectionNumber && (
                      <span className="text-[10px] text-gray-600 font-mono">
                        §{item.sectionNumber}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span
                      className={cls(
                        "text-[9px] px-1.5 py-0.5 rounded font-semibold",
                        delivery.cls,
                      )}
                    >
                      {delivery.label}
                    </span>
                    {item.qtyOrdered > 0 && (
                      <span className="text-[10px] text-gray-500">
                        {item.qtyDelivered}/{item.qtyOrdered} {item.uom}{" "}
                        delivered
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right space-y-1">
                  <p className="text-[10px] font-mono text-gray-300">
                    {item.uom}
                  </p>
                  {item.unitPrice > 0 && (
                    <p className="text-[10px] text-amber-400 font-mono">
                      ${fmt(item.unitPrice)}
                    </p>
                  )}
                  <span
                    className={cls(
                      "text-[9px] px-1.5 py-0.5 rounded font-semibold inline-block",
                      already
                        ? "bg-gray-600 text-gray-400"
                        : "bg-amber-900/50 text-amber-400",
                    )}
                  >
                    {already ? "Added" : "+ Add"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {query.trim().length >= 2 && results.length === 0 && !searching && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-gray-800 border border-gray-600 rounded-xl shadow-xl px-3 py-3 text-center">
          <p className="text-xs text-gray-500">No items found for "{query}"</p>
          <p className="text-[10px] text-gray-600 mt-0.5">
            Searches budget, change order, and manual items across all POs
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LABOR SEARCH INLINE
// ─────────────────────────────────────────────────────────────────────────────
function LaborSearchInline({ token, onSelect, placeholder }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/item-database`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: query, category: "L", limit: 12 },
        });
        const raw =
          res.data.items ||
          res.data.data ||
          (Array.isArray(res.data) ? res.data : []);
        setResults(
          raw.filter((i) => !i.category || i.category === "L").slice(0, 10),
        );
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, token]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg focus-within:border-blue-500 transition-colors">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder || "Search labor items (Item Database)…"}
          className="flex-1 text-xs bg-transparent text-gray-200 placeholder-gray-500 focus:outline-none"
        />
        {searching && (
          <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />
        )}
      </div>
      {results.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-30 mt-0.5 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl max-h-40 overflow-y-auto">
          {results.map((item) => (
            <button
              key={item._id || item.itemCode}
              onClick={() => {
                onSelect(item);
                setQuery("");
                setResults([]);
              }}
              className="w-full text-left px-2.5 py-2 flex items-center justify-between border-b border-gray-600/40 last:border-0 hover:bg-gray-600 transition-colors"
            >
              <div>
                <span className="text-[11px] font-mono text-blue-400">
                  {item.itemCode}
                </span>
                <span className="text-[11px] text-gray-300 ml-2">
                  {item.description || item.itemName}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-[10px] text-gray-500">{item.uom}</span>
                <span className="text-[10px] text-green-400 font-mono">
                  ${fmt(item.defaultCost)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAIRED ITEM ROW
// ─────────────────────────────────────────────────────────────────────────────
function PairedItemRow({
  item,
  idx,
  onChange,
  onRemove,
  onAttachLabor,
  token,
}) {
  const dimMode = getDimMode(item.material?.uom || item.labor?.uom || "");
  const isLaborOnly = !item.material;
  const set = (path, value) => onChange(idx, path, value);

  return (
    <div className="rounded-xl border border-gray-700 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-900/60 border-b border-gray-700">
        <span className="text-[10px] font-mono text-gray-600 w-5 shrink-0 text-center">
          {idx + 1}
        </span>
        <span
          className={cls(
            "text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide border",
            isLaborOnly
              ? "bg-blue-900/50 text-blue-300 border-blue-700/40"
              : "bg-amber-900/40 text-amber-300 border-amber-700/40",
          )}
        >
          {isLaborOnly ? "Labor Only" : "Material + Labor"}
        </span>
        {dimMode !== "qty" && (
          <span className="text-[9px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded border border-gray-600">
            {dimMode === "sf" ? "H × L = SF" : "L = LF"}
          </span>
        )}
        <div className="flex-1" />
        <button
          onClick={() => onRemove(idx)}
          className="text-gray-600 hover:text-red-400 transition-colors p-1 rounded"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
      <div className="p-3 space-y-3">
        {!isLaborOnly && (
          <div className="bg-amber-950/20 border border-amber-800/30 rounded-lg p-3 space-y-2">
            <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-wide">
              Material (from PO)
            </p>
            <div className="grid grid-cols-[80px_1fr_56px] gap-2">
              <div>
                <p className="text-[9px] text-gray-500 mb-1">PO Code</p>
                <input
                  value={item.material?.poItemCode || ""}
                  onChange={(e) => set("material.poItemCode", e.target.value)}
                  placeholder="PO-001"
                  className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded font-mono text-amber-300 placeholder-gray-600 focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <p className="text-[9px] text-gray-500 mb-1">Description</p>
                <input
                  value={item.material?.description || ""}
                  onChange={(e) => set("material.description", e.target.value)}
                  placeholder="e.g. 5/8″ Type-X Drywall 4×12"
                  className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-600 focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <p className="text-[9px] text-gray-500 mb-1">UOM</p>
                <input
                  value={item.material?.uom || ""}
                  onChange={(e) => set("material.uom", e.target.value)}
                  placeholder="SF"
                  className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded text-gray-300 placeholder-gray-600 focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>
            {dimMode === "sf" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[9px] text-gray-500 mb-1">
                    Default Height (ft)
                  </p>
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    value={item.material?.defaultHeight || ""}
                    onChange={(e) =>
                      set(
                        "material.defaultHeight",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    placeholder="e.g. 9"
                    className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-600 focus:border-amber-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <p className="text-[9px] text-gray-500 mb-1">
                    Default Length (ft)
                  </p>
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    value={item.material?.defaultLength || ""}
                    onChange={(e) =>
                      set(
                        "material.defaultLength",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    placeholder="e.g. 12"
                    className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-600 focus:border-amber-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>
            )}
            {dimMode === "lf" && (
              <div>
                <p className="text-[9px] text-gray-500 mb-1">
                  Default Length (ft)
                </p>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={item.material?.defaultLength || ""}
                  onChange={(e) =>
                    set(
                      "material.defaultLength",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  placeholder="e.g. 10"
                  className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-600 focus:border-amber-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            )}
            <p className="text-[9px] text-amber-700">
              Workers can override dimensions at submission time
            </p>
          </div>
        )}
        <div className="bg-blue-950/20 border border-blue-800/30 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wide">
              Labor Item
            </p>
            {!isLaborOnly && (
              <p className="text-[9px] text-blue-600">
                Qty auto-mirrors material dimensions
              </p>
            )}
          </div>
          <div className="grid grid-cols-[80px_1fr_56px] gap-2">
            <div>
              <p className="text-[9px] text-gray-500 mb-1">Item Code</p>
              <input
                value={item.labor?.itemCode || ""}
                onChange={(e) => set("labor.itemCode", e.target.value)}
                placeholder="L-001"
                className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded font-mono text-blue-300 placeholder-gray-600 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <p className="text-[9px] text-gray-500 mb-1">Description</p>
              <input
                value={item.labor?.description || ""}
                onChange={(e) => set("labor.description", e.target.value)}
                placeholder="e.g. Install 5/8″ Drywall"
                className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <p className="text-[9px] text-gray-500 mb-1">UOM</p>
              <input
                value={item.labor?.uom || ""}
                onChange={(e) => set("labor.uom", e.target.value)}
                placeholder="SF"
                className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded text-gray-300 placeholder-gray-600 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
          <div>
            <p className="text-[9px] text-gray-500 mb-1">Worker Pay Rate $</p>
            <input
              type="number"
              min="0"
              step="0.01"
              value={item.labor?.unitRate || ""}
              onChange={(e) =>
                set("labor.unitRate", parseFloat(e.target.value) || 0)
              }
              placeholder="0.00"
              className="w-full px-2 py-1 text-[11px] bg-gray-700 border border-gray-600 rounded font-mono text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULES PANEL helpers
// ─────────────────────────────────────────────────────────────────────────────
const WO_ACCENTS = [
  "#EA580C",
  "#2563EB",
  "#7C3AED",
  "#059669",
  "#D97706",
  "#DB2777",
  "#0891B2",
];

function getModuleWOs(mod) {
  if (Array.isArray(mod.workOrders) && mod.workOrders.length > 0)
    return mod.workOrders;
  if (mod.workOrder) return [mod.workOrder];
  return [];
}
function woId(wo) {
  return typeof wo === "object" ? wo._id : wo;
}
function woLabel(wo) {
  if (typeof wo === "object" && wo.woNumber)
    return `${wo.woNumber} — ${wo.title || ""}`;
  return String(wo);
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULES PANEL
// ─────────────────────────────────────────────────────────────────────────────
function ModulesPanel({ projectId, workOrders, token }) {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortMode, setSortMode] = useState("wo");
  const [collapsed, setCollapsed] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/pieceworkRequests/module-admin/list`,
        { headers, params: { projectId } },
      );
      setModules(res.data.modules || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const handleDelete = async (modId) => {
    setDeleting(modId);
    setDeleteConfirm(null);
    try {
      await axios.delete(`${API}/pieceworkRequests/module-admin/${modId}`, {
        headers,
      });
      setModules((p) => p.filter((m) => m._id !== modId));
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setDeleting(null);
    }
  };

  const toggleActive = async (mod) => {
    setModules((p) =>
      p.map((m) => (m._id === mod._id ? { ...m, isActive: !m.isActive } : m)),
    );
    try {
      await axios.put(
        `${API}/pieceworkRequests/module-admin/${mod._id}`,
        { isActive: !mod.isActive },
        { headers },
      );
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setModules((p) =>
        p.map((m) =>
          m._id === mod._id ? { ...m, isActive: mod.isActive } : m,
        ),
      );
    }
  };

  const goNew = () =>
    navigate("/modules/new", {
      state: {
        projectId,
        workOrders,
        returnTo: `/Projects/${projectId}?tab=Site+Requests`,
        returnTab: "modules",
      },
    });
  const goEdit = (mod) =>
    navigate(`/modules/${mod._id}/edit`, {
      state: {
        projectId,
        workOrders,
        returnTo: `/Projects/${projectId}?tab=Site+Requests`,
        returnTab: "modules",
      },
    });

  const woAccentMap = useMemo(() => {
    const map = {};
    const seen = new Set();
    modules.forEach((m) => {
      getModuleWOs(m).forEach((wo) => {
        const id = woId(wo);
        if (!seen.has(id)) {
          map[id] = WO_ACCENTS[seen.size % WO_ACCENTS.length];
          seen.add(id);
        }
      });
    });
    map["none"] = "#6B7280";
    return map;
  }, [modules]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return modules.filter((m) => {
      if (activeFilter === "active" && !m.isActive) return false;
      if (activeFilter === "inactive" && m.isActive) return false;
      if (!q) return true;
      const wos = getModuleWOs(m);
      const woText = wos
        .map((w) => (typeof w === "object" ? `${w.woNumber} ${w.title}` : w))
        .join(" ");
      return [
        m.name,
        woText,
        m.costCode?.code,
        m.costCode?.name,
        m.sectionDisplayName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [modules, activeFilter, search]);

  const handleDuplicate = async (mod) => {
    setError("");
    try {
      const res = await axios.get(
        `${API}/pieceworkRequests/module-admin/list`,
        { headers, params: { moduleId: mod._id } },
      );
      const full = (res.data.modules || []).find((m) => m._id === mod._id);
      if (!full) {
        setError("Could not load module to duplicate.");
        return;
      }
      const body = {
        name: `${full.name} (Copy)`,
        costCode: full.costCode || {},
        sectionDisplayName: full.sectionDisplayName || "",
        description: full.description || "",
        projectId: projectId || null,
        isActive: false,
        workOrderIds: Array.isArray(full.workOrders)
          ? full.workOrders.map((w) => (typeof w === "object" ? w._id : w))
          : full.workOrder
            ? [
                typeof full.workOrder === "object"
                  ? full.workOrder._id
                  : full.workOrder,
              ]
            : [],
        pairedItems: (full.pairedItems || []).map((p, i) => ({
          material: null,
          labor: { ...p.labor },
          suggestedQty: p.suggestedQty || 0,
          isActive: p.isActive !== false,
          sortOrder: i,
        })),
      };
      const created = await axios.post(
        `${API}/pieceworkRequests/module-admin`,
        body,
        { headers },
      );
      const newId = created.data?.module?._id || created.data?._id;
      if (newId) goEdit({ _id: newId });
      else fetchModules();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((m) => {
      if (sortMode === "wo") {
        const wos = getModuleWOs(m);
        if (wos.length === 0) {
          const key = "none";
          if (!map.has(key))
            map.set(key, {
              label: "All work orders",
              accent: woAccentMap["none"],
              items: [],
            });
          if (!map.get(key).items.find((x) => x._id === m._id))
            map.get(key).items.push(m);
        } else {
          wos.forEach((wo) => {
            const key = woId(wo);
            const label = woLabel(wo);
            const accent = woAccentMap[key] || WO_ACCENTS[0];
            if (!map.has(key)) map.set(key, { label, accent, items: [] });
            if (!map.get(key).items.find((x) => x._id === m._id))
              map.get(key).items.push(m);
          });
        }
      } else {
        const key = m.costCode?.code || "none";
        const label = m.costCode?.code
          ? `${m.costCode.code} — ${m.costCode.name}`
          : "No section";
        if (!map.has(key))
          map.set(key, { label, accent: "#6B7280", items: [] });
        map.get(key).items.push(m);
      }
    });
    return [...map.entries()]
      .map(([key, val]) => ({ key, ...val }))
      .sort((a, b) => {
        if (a.key === "none") return 1;
        if (b.key === "none") return -1;
        return a.label.localeCompare(b.label);
      });
  }, [filtered, sortMode, woAccentMap]);

  const activeCount = filtered.filter((m) => m.isActive).length;

  if (loading)
    return (
      <div className="flex items-center gap-2 py-10 justify-center">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500" />
        <span className="text-sm text-gray-400">Loading…</span>
      </div>
    );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="pl-8 pr-3 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500 w-44"
          />
        </div>
        <div className="flex p-0.5 bg-gray-700 rounded-lg">
          {["all", "active", "inactive"].map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={cls(
                "px-3 py-1 text-xs rounded-md capitalize transition-colors",
                activeFilter === f
                  ? "bg-gray-600 text-white"
                  : "text-gray-400 hover:text-gray-200",
              )}
            >
              {f}
            </button>
          ))}
        </div>
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value)}
          className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:outline-none cursor-pointer"
        >
          <option value="wo">By work order</option>
          <option value="section">By section</option>
        </select>
        <span className="ml-auto text-xs text-gray-500">
          <span className="text-gray-300 font-medium">{filtered.length}</span>{" "}
          module{filtered.length !== 1 ? "s" : ""}
          {" · "}
          <span className="text-gray-300 font-medium">{activeCount}</span>{" "}
          active
        </span>
        <button
          onClick={goNew}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
        >
          + New module
        </button>
      </div>
      {error && (
        <div className="px-3 py-2 bg-red-900/40 border border-red-700/50 rounded-lg text-xs text-red-300">
          {error}
        </div>
      )}
      {filtered.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-gray-600 rounded-xl text-sm text-gray-500">
          No modules match your filters
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map(({ key, label, accent, items }) => {
            const isCollapsed = collapsed[key] !== false;
            return (
              <div key={key}>
                <button
                  onClick={() =>
                    setCollapsed((p) => ({ ...p, [key]: !isCollapsed }))
                  }
                  className="w-full flex items-center gap-2.5 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-750 transition-colors mb-1"
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: accent }}
                  />
                  <span className="text-xs font-medium text-gray-200 flex-1 text-left">
                    {label}
                  </span>
                  <span className="text-[11px] text-gray-500 mr-1">
                    {items.length} module{items.length !== 1 ? "s" : ""}
                  </span>
                  <svg
                    className={cls(
                      "w-3.5 h-3.5 text-gray-500 transition-transform duration-150",
                      isCollapsed ? "" : "rotate-180",
                    )}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {!isCollapsed && (
                  <div className="rounded-xl border border-gray-700 overflow-hidden">
                    <div className="grid grid-cols-[3px_1fr_180px_120px_90px_100px_76px] bg-gray-900 border-b border-gray-700/80">
                      <div />
                      {[
                        "Module",
                        "Section",
                        "Work Orders",
                        "Items",
                        "Status",
                        "Actions",
                      ].map((h) => (
                        <div
                          key={h}
                          className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-widest first:px-4"
                        >
                          {h}
                        </div>
                      ))}
                    </div>
                    {items.map((mod) => {
                      const modWOs = getModuleWOs(mod);
                      const isAwaitingDelete = deleteConfirm === mod._id;
                      return (
                        <div
                          key={mod._id}
                          onClick={() => {
                            if (!isAwaitingDelete) goEdit(mod);
                          }}
                          className={cls(
                            "grid grid-cols-[3px_1fr_180px_120px_90px_100px_76px] border-b border-gray-300/40 last:border-0 group transition-colors",
                            !mod.isActive && "opacity-70",
                            isAwaitingDelete
                              ? "bg-red-900/20 cursor-default"
                              : "bg-gray-700 hover:bg-gray-600/80 cursor-pointer",
                          )}
                        >
                          <div
                            className="self-stretch"
                            style={{ background: accent }}
                          />
                          <div className="px-4 py-3 flex items-center min-w-0 gap-2">
                            <span className="text-[13px] font-semibold text-gray-100 truncate">
                              {mod.name}
                            </span>
                            <svg
                              className="w-3 h-3 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </div>
                          <div className="px-3 py-3 flex items-center gap-2 min-w-0">
                            {mod.costCode?.code && (
                              <span className="text-[10px] font-mono font-medium text-gray-500 bg-gray-800 border border-gray-700 px-1.5 py-0.5 rounded shrink-0">
                                {mod.costCode.code}
                              </span>
                            )}
                            <span className="text-[11px] text-gray-400 truncate">
                              {mod.sectionDisplayName ||
                                mod.costCode?.name ||
                                "—"}
                            </span>
                          </div>
                          <div className="px-3 py-3 flex items-center">
                            {modWOs.length === 0 ? (
                              <span className="text-[10px] text-gray-600 italic">
                                All WOs
                              </span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {modWOs.map((wo) => {
                                  const id = woId(wo);
                                  const pillAccent =
                                    woAccentMap[id] || "#6B7280";
                                  const num =
                                    typeof wo === "object"
                                      ? wo.woNumber
                                      : String(wo).slice(-4);
                                  return (
                                    <span
                                      key={id}
                                      title={
                                        typeof wo === "object"
                                          ? `${wo.woNumber} — ${wo.title}`
                                          : id
                                      }
                                      className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border"
                                      style={{
                                        borderColor: pillAccent + "60",
                                        background: pillAccent + "18",
                                        color: pillAccent,
                                      }}
                                    >
                                      {num}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="px-3 py-3 flex items-center">
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-800 text-gray-300 border border-gray-600">
                              {(mod.pairedItems || []).length} items
                            </span>
                          </div>
                          <div className="px-3 py-3 flex items-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleActive(mod);
                              }}
                              title={
                                mod.isActive
                                  ? "Click to deactivate"
                                  : "Click to activate"
                              }
                              className={cls(
                                "flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border transition-all",
                                mod.isActive
                                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25"
                                  : "bg-red-500/15 text-red-400 border-red-500/30 hover:bg-red-500/25",
                              )}
                            >
                              <span
                                className={cls(
                                  "w-1.5 h-1.5 rounded-full shrink-0",
                                  mod.isActive
                                    ? "bg-emerald-400"
                                    : "bg-red-400",
                                )}
                              />
                              {mod.isActive ? "Active" : "Inactive"}
                            </button>
                          </div>
                          <div
                            className="px-3 py-3 flex items-center justify-end gap-0.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isAwaitingDelete ? (
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-red-400 font-medium mr-1 whitespace-nowrap">
                                  Sure?
                                </span>
                                <button
                                  onClick={() => handleDelete(mod._id)}
                                  disabled={deleting === mod._id}
                                  className="px-2 py-0.5 text-[10px] font-bold text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-40 transition-colors whitespace-nowrap"
                                >
                                  {deleting === mod._id ? "…" : "Yes"}
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(null)}
                                  className="px-2 py-0.5 text-[10px] font-medium text-gray-300 bg-gray-600 rounded hover:bg-gray-500 transition-colors"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleDuplicate(mod)}
                                  className="p-1.5 text-gray-500 hover:text-violet-400 transition-colors rounded-lg hover:bg-violet-400/10 opacity-0 group-hover:opacity-100"
                                  title="Duplicate module"
                                >
                                  <svg
                                    className="w-3.5 h-3.5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => goEdit(mod)}
                                  className="p-1.5 text-gray-500 hover:text-blue-400 transition-colors rounded-lg hover:bg-blue-400/10 opacity-0 group-hover:opacity-100"
                                  title="Edit"
                                >
                                  <svg
                                    className="w-3.5 h-3.5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(mod._id)}
                                  disabled={deleting === mod._id}
                                  className="p-1.5 text-gray-500 hover:text-red-400 transition-colors rounded-lg hover:bg-red-400/10 disabled:opacity-40 opacity-0 group-hover:opacity-100"
                                  title="Delete"
                                >
                                  <svg
                                    className="w-3.5 h-3.5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FILTER DROPDOWN COMPONENT — portal-based so it escapes overflow:auto clipping
// ─────────────────────────────────────────────────────────────────────────────
function FilterDropdown({
  label,
  col,
  sort,
  onSort,
  activeCount = 0,
  open,
  onToggle,
  onClose,
  children,
  align = "left",
}) {
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 210 });

  // Recompute position whenever the dropdown opens
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropW = 230;
    let left = align === "right" ? rect.right - dropW : rect.left;
    // clamp to viewport
    if (left + dropW > window.innerWidth - 8)
      left = window.innerWidth - dropW - 8;
    if (left < 8) left = 8;
    setPos({
      top: rect.bottom + window.scrollY + 6,
      left: left + window.scrollX,
      width: dropW,
    });
  }, [open, align]);

  // Close on outside click (checks both trigger and portal dropdown)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const isSorted = sort?.col === col;

  const portal = open
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: pos.top,
            left: pos.left,
            width: pos.width,
            zIndex: 99999,
            background: "#1a1f2e",
            border: "1px solid #4b5563",
            borderRadius: "0.75rem",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            overflow: "hidden",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>,
        document.body,
      )
    : null;

  return (
    <div
      ref={triggerRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        onClick={() => {
          if (col) onSort(col);
          onToggle();
        }}
        className={cls(
          "flex items-center gap-1.5 text-left select-none transition-colors whitespace-nowrap group",
          activeCount > 0
            ? "text-orange-400"
            : open
              ? "text-gray-100"
              : "text-gray-400 hover:text-gray-200",
        )}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          {label}
        </span>

        {col && (
          <span className="inline-flex flex-col leading-[0] gap-[1px]">
            <svg
              className={cls(
                "w-2 h-2",
                isSorted && sort.dir === "asc"
                  ? "text-orange-400"
                  : "text-gray-700",
              )}
              viewBox="0 0 10 6"
              fill="currentColor"
            >
              <path d="M5 0L10 6H0z" />
            </svg>
            <svg
              className={cls(
                "w-2 h-2",
                isSorted && sort.dir === "desc"
                  ? "text-orange-400"
                  : "text-gray-700",
              )}
              viewBox="0 0 10 6"
              fill="currentColor"
            >
              <path d="M5 6L0 0h10z" />
            </svg>
          </span>
        )}

        {/* Funnel icon */}
        <svg
          className={cls(
            "w-3 h-3 shrink-0 transition-colors",
            activeCount > 0
              ? "text-orange-400"
              : "text-gray-600 group-hover:text-gray-400",
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M3 3a1 1 0 011-1h12a1 1 0 01.707 1.707L13 8.414V15a1 1 0 01-.553.894l-4 2A1 1 0 017 17v-8.586L3.293 4.707A1 1 0 013 4V3z"
            clipRule="evenodd"
          />
        </svg>

        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold rounded-full bg-orange-500 text-white shrink-0">
            {activeCount}
          </span>
        )}
      </button>

      {portal}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TABLE FILTERS HOOK  — persists to localStorage, supports multi-select arrays
// ─────────────────────────────────────────────────────────────────────────────
function useTableFilters(projectId) {
  const STORAGE_KEY = `pw_requests_filters_v2_${projectId}`;

  const defaults = {
    sort: { col: "submittedAt", dir: "desc" },
    cols: {
      requestNumber: "",
      status: [],
      workOrder: [],
      worker: [],
      workDate: "",
      tags: [],
      module: [],
      notes: "",
    },
  };

  const load = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const saved = load();

  const [sort, setSort] = useState(saved?.sort || defaults.sort);
  const [cols, setCols] = useState({
    ...defaults.cols,
    ...(saved?.cols || {}),
    status: Array.isArray(saved?.cols?.status) ? saved.cols.status : [],
    workOrder: Array.isArray(saved?.cols?.workOrder)
      ? saved.cols.workOrder
      : [],
    tags: Array.isArray(saved?.cols?.tags) ? saved.cols.tags : [],
    module: Array.isArray(saved?.cols?.module) ? saved.cols.module : [],
    worker: Array.isArray(saved?.cols?.worker) ? saved.cols.worker : [],
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ sort, cols }));
    } catch {}
  }, [sort, cols]);

  const toggleSort = (col) =>
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" },
    );

  const setCol = (key, val) => setCols((prev) => ({ ...prev, [key]: val }));

  const toggleColValue = (key, val) =>
    setCols((prev) => {
      const arr = Array.isArray(prev[key]) ? prev[key] : [];
      return {
        ...prev,
        [key]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val],
      };
    });

  const clearAll = () => {
    setSort(defaults.sort);
    setCols(defaults.cols);
  };

  const hasActiveFilters =
    cols.requestNumber !== "" ||
    cols.notes !== "" ||
    cols.workDate !== "" ||
    (Array.isArray(cols.worker) && cols.worker.length > 0) ||
    (Array.isArray(cols.status) && cols.status.length > 0) ||
    (Array.isArray(cols.workOrder) && cols.workOrder.length > 0) ||
    (Array.isArray(cols.module) && cols.module.length > 0) ||
    (Array.isArray(cols.tags) && cols.tags.length > 0);
  return {
    sort,
    cols,
    toggleSort,
    setCol,
    toggleColValue,
    clearAll,
    hasActiveFilters,
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function PieceworkRequestsTab({
  projectId,
  projectName,
  onLogCreated,
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const _storedRole =
    localStorage.getItem("userRole") || localStorage.getItem("role") || "";
  const headers = { Authorization: `Bearer ${token}` };
  const location = useLocation();
  const [confirmedRole, setConfirmedRole] = useState(_storedRole);
  const [retagging, setRetagging] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("submitted");
  const [acting, setActing] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [expandedRequest, setExpandedRequest] = useState(null);
  const isEditingInModal = useRef(false);

  const [successMsg, setSuccessMsg] = useState("");
  const [openFilterCol, setOpenFilterCol] = useState(null);

  const [activeView, setActiveView] = useState(
    location.state?.siteRequestsView ||
      (location.state?.returnTab === "modules" ? "modules" : "requests"),
  );

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { headers })
      .then((r) => {
        const role = r.data?.user?.role || r.data?.user?.userRole || "";
        if (role) setConfirmedRole(role);
      })
      .catch(() => {});
  }, []);

  const isManager =
    confirmedRole === "admin" || confirmedRole === "project_manager";

  const [workOrders, setWorkOrders] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/workorders3/project/${projectId}`, { headers })
      .then((r) =>
        setWorkOrders(
          (r.data.workOrders || []).filter((w) =>
            ["piecework", "union_piecework"].includes(w.type),
          ),
        ),
      )
      .catch(() => {});
  }, [projectId]);

  const handleUpdated = (updated) =>
    setRequests((prev) =>
      prev.map((r) => (r._id === updated._id ? updated : r)),
    );

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const pageSize = 100;
      let page = 1;
      let all = [];
      let total = Infinity;
      while (all.length < total) {
        const res = await axios.get(`${API}/pieceworkRequests`, {
          headers,
          params: { project: projectId, limit: pageSize, page },
        });
        const batch = res.data.data || [];
        all = all.concat(batch);
        total = res.data.pagination?.total ?? all.length;
        if (batch.length < pageSize) break;
        page += 1;
      }
      setRequests(all);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const {
    sort,
    cols,
    toggleSort,
    setCol,
    toggleColValue,
    clearAll,
    hasActiveFilters,
  } = useTableFilters(projectId);

  // ── Requests scoped to the active status tab (used for option lists + counts) ──
  // Options and counts only reflect what's actually visible under the current tab.
  const scopedRequests = useMemo(
    () =>
      statusFilter === "all"
        ? requests
        : requests.filter((r) => r.status === statusFilter),
    [requests, statusFilter],
  );

  // ── Derived option lists for dropdown filters ──────────────────────────────
  const statusOptions = useMemo(
    () =>
      [...new Set(scopedRequests.map((r) => r.status).filter(Boolean))].sort(),
    [scopedRequests],
  );

  const workOrderOptions = useMemo(() => {
    const map = new Map();
    scopedRequests.forEach((r) => {
      if (r.workOrder?._id)
        map.set(
          r.workOrder._id,
          r.workOrder.title || r.workOrder.woNumber || r.workOrder._id,
        );
    });
    return [...map.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [scopedRequests]);

  const moduleOptions = useMemo(
    () =>
      [
        ...new Set(scopedRequests.map((r) => r.moduleName).filter(Boolean)),
      ].sort(),
    [scopedRequests],
  );

  const workerOptions = useMemo(() => {
    const map = new Map();
    scopedRequests.forEach((r) => {
      if (r.worker?._id || r.worker?.email) {
        const id = r.worker._id || r.worker.email;
        const label = r.worker.displayName || r.worker.email || id;
        map.set(id, label);
      }
    });
    return [...map.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [scopedRequests]);

  const [dupOnly, setDupOnly] = useState(false);

  const tagOptions = useMemo(
    () => buildTagOptions(scopedRequests),
    [scopedRequests],
  );
  const flaggedCount = useMemo(
    () =>
      requests.filter(
        (r) => r.status === "submitted" && r.hasUnresolvedDuplicate,
      ).length,
    [requests],
  );

  // ── Filtered + sorted list ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list =
      statusFilter === "all"
        ? requests
        : requests.filter((r) => r.status === statusFilter);

    if (cols.requestNumber)
      list = list.filter((r) =>
        (r.requestNumber || "")
          .toLowerCase()
          .includes(cols.requestNumber.toLowerCase()),
      );

    if (Array.isArray(cols.status) && cols.status.length > 0)
      list = list.filter((r) => cols.status.includes(r.status));

    if (Array.isArray(cols.workOrder) && cols.workOrder.length > 0)
      list = list.filter((r) => cols.workOrder.includes(r.workOrder?._id));

    if (Array.isArray(cols.worker) && cols.worker.length > 0)
      list = list.filter((r) =>
        cols.worker.includes(r.worker?._id || r.worker?.email),
      );

    if (cols.workDate)
      list = list.filter((r) =>
        (r.workCompletedDate || "").includes(cols.workDate),
      );

    if (Array.isArray(cols.module) && cols.module.length > 0)
      list = list.filter((r) => cols.module.includes(r.moduleName));

    if (cols.notes) list = list.filter((r) => matchesTextQuery(r, cols.notes));
    if (cols.tags.length) list = list.filter((r) => hasAllTags(r, cols.tags));
    if (dupOnly) list = list.filter((r) => r.hasUnresolvedDuplicate);

    list = [...list].sort((a, b) => {
      let av, bv;
      switch (sort.col) {
        case "requestNumber":
          av = a.requestNumber || "";
          bv = b.requestNumber || "";
          break;
        case "logNumber":
          av = a.promotedLogId?.logNumber || "";
          bv = b.promotedLogId?.logNumber || "";
          break;
        case "status":
          av = a.status || "";
          bv = b.status || "";
          break;
        case "workOrder":
          av = a.workOrder?.woNumber || "";
          bv = b.workOrder?.woNumber || "";
          break;
        case "worker":
          av = a.worker?.displayName || a.worker?.email || "";
          bv = b.worker?.displayName || b.worker?.email || "";
          break;
        case "items":
          av = (a.lineItems || []).filter(
            (i) => !i.poItemCode && !!i.itemCode,
          ).length;
          bv = (b.lineItems || []).filter(
            (i) => !i.poItemCode && !!i.itemCode,
          ).length;
          break;
        case "workDate":
          av = a.workCompletedDate || "";
          bv = b.workCompletedDate || "";
          break;
        case "submittedAt":
          av = a.submittedAt || "";
          bv = b.submittedAt || "";
          break;
        case "subtotal":
          av = a.totalSubtotal || 0;
          bv = b.totalSubtotal || 0;
          break;
        case "total":
          av = a.totalCost || 0;
          bv = b.totalCost || 0;
          break;
        default:
          av = a.submittedAt || "";
          bv = b.submittedAt || "";
      }
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [requests, statusFilter, sort, cols, dupOnly]);

  const counts = useMemo(
    () => ({
      submitted: requests.filter((r) => r.status === "submitted").length,
      confirmed: requests.filter((r) => r.status === "confirmed").length,
      rejected: requests.filter((r) => r.status === "rejected").length,
    }),
    [requests],
  );

  const handleConfirm = async (requestId) => {
    setActing(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/pieceworkRequests/${requestId}/confirm`,
        {},
        { headers },
      );
      const logNum = res.data.data?.log?.logNumber;
      setSuccessMsg(`Log ${logNum} created — pending PM approval.`);
      await fetchRequests();
      if (onLogCreated) onLogCreated(res.data.data.log);
      setTimeout(() => setSuccessMsg(""), 5000);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setActing(false);
    }
  };

  const ackUrl = (id) => `${API}/pieceworkRequests/${id}/duplicates/ack`;

  const dupLines = (open) =>
    open
      .map(
        (d) =>
          `• ${d.requestNumber} (${d.status}) — unit ${d.sharedUnits.join(", ")}, ${d.workerName}, ${(d.workCompletedDate || "").slice(0, 10)}`,
      )
      .join("\n");

  const handleDuplicateClick = async (req) => {
    const open = (req.duplicates || []).filter((d) => !d.acknowledged);
    if (!open.length) return;
    if (
      !window.confirm(
        `${req.requestNumber} may duplicate:\n\n${dupLines(open)}\n\nOK = mark as reviewed (not a duplicate)\nCancel = leave flagged`,
      )
    )
      return;
    try {
      await axios.post(ackUrl(req._id), {}, { headers });
      await fetchRequests();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const handleConfirmGuarded = async (req) => {
    const open = (req.duplicates || []).filter((d) => !d.acknowledged);
    if (open.length) {
      if (
        !window.confirm(
          `Possible duplicate\n\n${req.requestNumber} shares a unit and work selection with:\n\n${dupLines(open)}\n\nConfirm anyway?`,
        )
      )
        return;
      try {
        await axios.post(ackUrl(req._id), {}, { headers });
      } catch {}
    }
    return handleConfirm(req._id);
  };

  const handleRetag = async () => {
    if (
      !window.confirm(
        "Re-tag all requests in this project that haven't been tagged yet?\n\nThis runs in the background and can take several minutes for large projects.",
      )
    )
      return;
    setRetagging(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/pieceworkRequests/retag`,
        { projectId, onlyMissing: true, limit: 1000 },
        { headers },
      );
      const queued = res.data?.queued ?? 0;
      if (!queued) {
        setSuccessMsg("All requests are already tagged.");
      } else {
        // ~1.5s per request (Groq call + 400ms rate-limit delay)
        const mins = Math.max(1, Math.ceil((queued * 1.5) / 60));
        setSuccessMsg(
          `Re-tagging ${queued} request${queued !== 1 ? "s" : ""} in the background (~${mins} min). Hit Refresh later to see tags.`,
        );
      }
      setTimeout(() => setSuccessMsg(""), 10000);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setRetagging(false);
    }
  };

  const handleRejectConfirm = async (reason) => {
    setRejecting(true);
    try {
      const ids = Array.isArray(rejectTarget) ? rejectTarget : [rejectTarget];
      await Promise.all(
        ids.map((id) =>
          axios.post(
            `${API}/pieceworkRequests/${id}/reject`,
            { reason },
            { headers },
          ),
        ),
      );
      setRejectTarget(null);
      setSelected(new Set());
      await fetchRequests();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setRejecting(false);
    }
  };

  // Shared sort-arrow helper
  const SortArrows = ({ col: c }) => (
    <span className="inline-flex flex-col leading-[0] gap-[1px]">
      <svg
        className={cls(
          "w-2 h-2",
          sort.col === c && sort.dir === "asc"
            ? "text-orange-400"
            : "text-gray-700",
        )}
        viewBox="0 0 10 6"
        fill="currentColor"
      >
        <path d="M5 0L10 6H0z" />
      </svg>
      <svg
        className={cls(
          "w-2 h-2",
          sort.col === c && sort.dir === "desc"
            ? "text-orange-400"
            : "text-gray-700",
        )}
        viewBox="0 0 10 6"
        fill="currentColor"
      >
        <path d="M5 6L0 0h10z" />
      </svg>
    </span>
  );

  return (
    <div className="space-y-4">
      <RejectModal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleRejectConfirm}
        submitting={rejecting}
      />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-gray-700 rounded-lg px-4 py-3">
        <div>
          <h2 className="text-base font-semibold text-white">
            Site Piecework Requests
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Submitted by workers — confirm to create a pending log for PM
            approval
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              navigate("/piecework/submit", {
                state: {
                  projectId,
                  projectName: projectName || "",
                  workOrders,
                },
              })
            }
            className="px-3 py-1.5 text-xs font-medium text-white bg-green-700 border border-green-600 rounded-lg hover:bg-green-600 transition-colors"
          >
            + Submit New Request
          </button>
          {activeView === "requests" && (
            <button
              onClick={fetchRequests}
              className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-600 border border-gray-500 rounded-lg hover:bg-gray-500 transition-colors"
            >
              ↻ Refresh
            </button>
          )}
          {isManager && activeView === "requests" && (
            <button
              onClick={handleRetag}
              disabled={retagging}
              title="Extract unit numbers and AI tags from notes on older requests"
              className="px-3 py-1.5 text-xs font-medium text-orange-300 bg-orange-900/30 border border-orange-700/50 rounded-lg hover:bg-orange-900/50 disabled:opacity-40 transition-colors"
            >
              {retagging ? "Starting…" : "⚡ Retag Requests"}
            </button>
          )}
        </div>
      </div>

      {/* View switcher */}
      {isManager && (
        <div className="flex gap-1 p-1 bg-gray-700 rounded-lg w-fit">
          {[
            { id: "requests", label: "Requests" },
            { id: "modules", label: "Modules" },
          ].map((v) => (
            <button
              key={v.id}
              onClick={() => setActiveView(v.id)}
              className={cls(
                "px-4 py-1.5 text-xs font-medium rounded-md transition-colors",
                activeView === v.id
                  ? "bg-gray-600 text-white shadow-sm"
                  : "text-gray-400 hover:text-gray-200",
              )}
            >
              {v.label}
              {v.id === "requests" && counts.submitted > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full font-bold bg-yellow-700/60 text-yellow-200">
                  {counts.submitted}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Modules view */}
      {activeView === "modules" && isManager && (
        <ModulesPanel
          projectId={projectId}
          workOrders={workOrders}
          token={token}
        />
      )}

      {/* Requests view */}
      {activeView === "requests" && (
        <>
          {/* Status tabs + clear */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1 p-1 bg-gray-700 rounded-lg">
              {STATUS_TABS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={cls(
                    "px-3 py-1.5 text-xs font-medium rounded-md transition-colors capitalize",
                    statusFilter === s
                      ? "bg-gray-600 text-white shadow-sm"
                      : "text-gray-400 hover:text-gray-200",
                  )}
                >
                  {s}
                  {s !== "all" && counts[s] > 0 && (
                    <span
                      className={cls(
                        "ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full font-bold",
                        s === "submitted"
                          ? "bg-yellow-700/60 text-yellow-200"
                          : s === "confirmed"
                            ? "bg-green-700/60 text-green-200"
                            : "bg-red-800/60 text-red-200",
                      )}
                    >
                      {counts[s]}
                    </span>
                  )}
                </button>
              ))}
              {flaggedCount > 0 && (
                <button
                  onClick={() => setDupOnly((p) => !p)}
                  className={cls(
                    "px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors",
                    dupOnly
                      ? "bg-yellow-600 border-yellow-500 text-white"
                      : "bg-yellow-900/30 border-yellow-700/50 text-yellow-300 hover:bg-yellow-900/50",
                  )}
                >
                  ⚠ Possible duplicates · {flaggedCount}
                </button>
              )}
            </div>
          </div>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 flex-wrap px-3 py-2.5 bg-gray-800/70 border border-gray-700 rounded-xl">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider shrink-0">
                Filters:
              </span>

              {cols.requestNumber && (
                <span className="inline-flex items-center gap-1 text-[11px] bg-gray-700 border border-gray-600 text-gray-200 px-2.5 py-1 rounded-full">
                  Req # "{cols.requestNumber}"
                  <button
                    onClick={() => setCol("requestNumber", "")}
                    className="text-gray-500 hover:text-white ml-0.5 leading-none"
                  >
                    ×
                  </button>
                </span>
              )}

              {Array.isArray(cols.status) &&
                cols.status.map((v) => (
                  <span
                    key={v}
                    className={cls(
                      "inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border capitalize",
                      STATUS_BADGE[v] || "bg-gray-700 text-gray-400",
                      "border-transparent",
                    )}
                  >
                    {v}
                    <button
                      onClick={() => toggleColValue("status", v)}
                      className="text-current opacity-60 hover:opacity-100 ml-0.5 leading-none"
                    >
                      ×
                    </button>
                  </span>
                ))}

              {Array.isArray(cols.workOrder) &&
                cols.workOrder.map((id) => {
                  const wo = workOrderOptions.find((w) => w.id === id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 text-[11px] bg-gray-700 border border-gray-600 text-gray-200 px-2.5 py-1 rounded-full"
                    >
                      WO: {wo?.label || id}
                      <button
                        onClick={() => toggleColValue("workOrder", id)}
                        className="text-gray-500 hover:text-white ml-0.5 leading-none"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}

              {Array.isArray(cols.worker) &&
                cols.worker.map((id) => {
                  const w = workerOptions.find((o) => o.id === id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 text-[11px] bg-gray-700 border border-gray-600 text-gray-200 px-2.5 py-1 rounded-full"
                    >
                      {w?.label || id}
                      <button
                        onClick={() => toggleColValue("worker", id)}
                        className="text-gray-500 hover:text-white ml-0.5 leading-none"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}

              {Array.isArray(cols.module) &&
                cols.module.map((v) => (
                  <span
                    key={v}
                    className="inline-flex items-center gap-1 text-[11px] bg-purple-900/40 border border-purple-700/40 text-purple-300 px-2.5 py-1 rounded-full"
                  >
                    {v}
                    <button
                      onClick={() => toggleColValue("module", v)}
                      className="text-purple-400 hover:text-white ml-0.5 leading-none"
                    >
                      ×
                    </button>
                  </span>
                ))}

              {cols.tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 text-[11px] bg-orange-900/30 border border-orange-700/40 text-orange-300 px-2.5 py-1 rounded-full"
                >
                  #{t}
                  <button
                    onClick={() => toggleColValue("tags", t)}
                    className="text-orange-400 hover:text-white ml-0.5 leading-none"
                  >
                    ×
                  </button>
                </span>
              ))}

              {cols.notes && (
                <span className="inline-flex items-center gap-1 text-[11px] bg-gray-700 border border-gray-600 text-gray-200 px-2.5 py-1 rounded-full">
                  Notes: "{cols.notes}"
                  <button
                    onClick={() => setCol("notes", "")}
                    className="text-gray-500 hover:text-white ml-0.5 leading-none"
                  >
                    ×
                  </button>
                </span>
              )}

              <button
                onClick={clearAll}
                className="ml-auto flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold text-orange-400 bg-orange-900/20 border border-orange-700/40 rounded-lg hover:bg-orange-900/40 transition-colors shrink-0"
              >
                <svg
                  className="w-3 h-3"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Clear all
              </button>
            </div>
          )}

          {error && (
            <div className="px-4 py-3 bg-red-900/40 border border-red-700/50 rounded-xl text-sm text-red-300">
              {error}
            </div>
          )}
          {successMsg && (
            <div className="px-4 py-3 bg-green-900/40 border border-green-700/50 rounded-xl text-sm text-green-300">
              ✓ {successMsg}
            </div>
          )}

          {/* Batch action bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-xl">
              <span className="text-sm text-gray-300 font-medium">
                {selected.size} selected
              </span>
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs text-gray-500 hover:text-gray-300"
              >
                Clear
              </button>
              <div className="flex-1" />
              {isManager && (
                <>
                  <button
                    onClick={async () => {
                      setActing(true);
                      setError("");
                      try {
                        const ids = [...selected].filter(
                          (id) =>
                            requests.find((r) => r._id === id)?.status ===
                            "submitted",
                        );
                        const flaggedSel = ids.filter(
                          (id) =>
                            requests.find((r) => r._id === id)
                              ?.hasUnresolvedDuplicate,
                        );
                        if (
                          flaggedSel.length &&
                          !window.confirm(
                            `${flaggedSel.length} selected request(s) are flagged as possible duplicates. Confirm all anyway?`,
                          )
                        ) {
                          setActing(false);
                          return;
                        }
                        for (const id of ids)
                          await axios.post(
                            `${API}/pieceworkRequests/${id}/confirm`,
                            {},
                            { headers },
                          );
                        setSelected(new Set());
                        await fetchRequests();
                        setSuccessMsg(`${ids.length} request(s) confirmed.`);
                        setTimeout(() => setSuccessMsg(""), 5000);
                      } catch (e) {
                        setError(e.response?.data?.message || e.message);
                      } finally {
                        setActing(false);
                      }
                    }}
                    disabled={
                      acting ||
                      ![...selected].some(
                        (id) =>
                          requests.find((r) => r._id === id)?.status ===
                          "submitted",
                      )
                    }
                    className="px-4 py-1.5 text-xs font-semibold text-white bg-green-700 rounded-lg hover:bg-green-600 disabled:opacity-40 transition-colors"
                  >
                    {acting
                      ? "Confirming…"
                      : `Confirm ${[...selected].filter((id) => requests.find((r) => r._id === id)?.status === "submitted").length}`}
                  </button>
                  <button
                    onClick={() =>
                      setRejectTarget(
                        [...selected].filter(
                          (id) =>
                            requests.find((r) => r._id === id)?.status ===
                            "submitted",
                        ),
                      )
                    }
                    disabled={
                      acting ||
                      ![...selected].some(
                        (id) =>
                          requests.find((r) => r._id === id)?.status ===
                          "submitted",
                      )
                    }
                    className="px-4 py-1.5 text-xs font-semibold text-white bg-red-700 rounded-lg hover:bg-red-600 disabled:opacity-40 transition-colors"
                  >
                    {`Reject ${[...selected].filter((id) => requests.find((r) => r._id === id)?.status === "submitted").length}`}
                  </button>
                </>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
              <span className="ml-2 text-sm text-gray-400">
                Loading requests…
              </span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-10 text-center">
              <p className="text-gray-500 text-sm">
                {statusFilter === "submitted"
                  ? "No pending requests to review."
                  : `No ${statusFilter} requests.`}
                {hasActiveFilters && " (some filters are active)"}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearAll}
                  className="mt-2 text-xs text-orange-400 hover:text-orange-300 underline underline-offset-2"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  {/* ── THEAD with dropdown filters ── */}
                  <thead className="bg-gray-900 border-b border-gray-700/80">
                    <tr>
                      {/* Checkbox */}
                      <th className="px-3 py-3 w-8">
                        <input
                          type="checkbox"
                          checked={
                            filtered.filter((r) => r.status === "submitted")
                              .length > 0 &&
                            filtered
                              .filter((r) => r.status === "submitted")
                              .every((r) => selected.has(r._id))
                          }
                          onChange={() => {
                            const submittedIds = filtered
                              .filter((r) => r.status === "submitted")
                              .map((r) => r._id);
                            const allChecked = submittedIds.every((id) =>
                              selected.has(id),
                            );
                            if (allChecked) {
                              setSelected((p) => {
                                const n = new Set(p);
                                submittedIds.forEach((id) => n.delete(id));
                                return n;
                              });
                            } else {
                              setSelected((p) => {
                                const n = new Set(p);
                                submittedIds.forEach((id) => n.add(id));
                                return n;
                              });
                            }
                          }}
                          className="rounded border-gray-500 bg-gray-700"
                        />
                      </th>

                      {/* Req # — text search */}
                      <th className="px-3 py-3">
                        <FilterDropdown
                          label="Req #"
                          col="requestNumber"
                          sort={sort}
                          onSort={toggleSort}
                          activeCount={cols.requestNumber ? 1 : 0}
                          open={openFilterCol === "requestNumber"}
                          onToggle={() =>
                            setOpenFilterCol((p) =>
                              p === "requestNumber" ? null : "requestNumber",
                            )
                          }
                          onClose={() => setOpenFilterCol(null)}
                        >
                          <div className="p-3 space-y-2">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                              Search
                            </p>
                            <div className="relative">
                              <input
                                autoFocus
                                value={cols.requestNumber}
                                onChange={(e) =>
                                  setCol("requestNumber", e.target.value)
                                }
                                placeholder="e.g. PR-0042"
                                className="w-full pl-2.5 pr-7 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                              />
                              {cols.requestNumber && (
                                <button
                                  onClick={() => setCol("requestNumber", "")}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-200 text-sm leading-none"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        </FilterDropdown>
                      </th>

                      {/* Status — multi-select */}
                      <th className="px-3 py-3">
                        <FilterDropdown
                          label="Status"
                          col="status"
                          sort={sort}
                          onSort={toggleSort}
                          activeCount={cols.status.length}
                          open={openFilterCol === "status"}
                          onToggle={() =>
                            setOpenFilterCol((p) =>
                              p === "status" ? null : "status",
                            )
                          }
                          onClose={() => setOpenFilterCol(null)}
                        >
                          <div className="p-2">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2 pt-1.5 pb-2">
                              Filter by status
                            </p>
                            <div className="space-y-0.5">
                              {statusOptions.map((val) => (
                                <label
                                  key={val}
                                  className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-700/60 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={cols.status.includes(val)}
                                    onChange={() =>
                                      toggleColValue("status", val)
                                    }
                                    className="rounded border-gray-500 bg-gray-700 text-orange-500 focus:ring-0 shrink-0"
                                  />
                                  <span
                                    className={cls(
                                      "text-[11px] capitalize px-2 py-0.5 rounded-full font-semibold",
                                      STATUS_BADGE[val] ||
                                        "bg-gray-700 text-gray-400",
                                    )}
                                  >
                                    {val}
                                  </span>
                                  <span className="text-[10px] text-gray-600 ml-auto">
                                    {
                                      scopedRequests.filter(
                                        (r) => r.status === val,
                                      ).length
                                    }
                                  </span>
                                </label>
                              ))}
                              {statusOptions.length === 0 && (
                                <p className="text-xs text-gray-600 px-2 py-2">
                                  No data
                                </p>
                              )}
                            </div>
                            {cols.status.length > 0 && (
                              <div className="border-t border-gray-700/60 mt-1.5 pt-1.5 px-2">
                                <button
                                  onClick={() => setCol("status", [])}
                                  className="text-[11px] text-orange-400 hover:text-orange-300"
                                >
                                  Clear
                                </button>
                              </div>
                            )}
                          </div>
                        </FilterDropdown>
                      </th>

                      {/* Log # — sortable, populated once the request is confirmed */}
                      <th
                        className="px-3 py-3 text-left cursor-pointer hover:text-gray-200 select-none whitespace-nowrap"
                        onClick={() => toggleSort("logNumber")}
                      >
                        <span
                          className={cls(
                            "inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider",
                            sort.col === "logNumber"
                              ? "text-orange-400"
                              : "text-gray-400",
                          )}
                        >
                          Log # <SortArrows col="logNumber" />
                        </span>
                      </th>

                      {/* Work Order — multi-select */}
                      <th className="px-3 py-3">
                        <FilterDropdown
                          label="Work Order"
                          col="workOrder"
                          sort={sort}
                          onSort={toggleSort}
                          activeCount={cols.workOrder.length}
                          open={openFilterCol === "workOrder"}
                          onToggle={() =>
                            setOpenFilterCol((p) =>
                              p === "workOrder" ? null : "workOrder",
                            )
                          }
                          onClose={() => setOpenFilterCol(null)}
                        >
                          <div className="p-2 max-h-60 overflow-y-auto">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2 pt-1.5 pb-2">
                              Filter by work order
                            </p>
                            <div className="space-y-0.5">
                              {workOrderOptions.map(({ id, label }) => (
                                <label
                                  key={id}
                                  className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-700/60 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={cols.workOrder.includes(id)}
                                    onChange={() =>
                                      toggleColValue("workOrder", id)
                                    }
                                    className="rounded border-gray-500 bg-gray-700 text-orange-500 focus:ring-0 shrink-0"
                                  />
                                  <span className="text-xs text-gray-200 truncate flex-1">
                                    {label}
                                  </span>
                                  <span className="text-[10px] text-gray-600 shrink-0">
                                    {
                                      scopedRequests.filter(
                                        (r) => r.workOrder?._id === id,
                                      ).length
                                    }
                                  </span>
                                </label>
                              ))}
                              {workOrderOptions.length === 0 && (
                                <p className="text-xs text-gray-600 px-2 py-2">
                                  No work orders
                                </p>
                              )}
                            </div>
                            {cols.workOrder.length > 0 && (
                              <div className="border-t border-gray-700/60 mt-1.5 pt-1.5 px-2">
                                <button
                                  onClick={() => setCol("workOrder", [])}
                                  className="text-[11px] text-orange-400 hover:text-orange-300"
                                >
                                  Clear
                                </button>
                              </div>
                            )}
                          </div>
                        </FilterDropdown>
                      </th>

                      {/* Worker — multi-select dropdown */}
                      <th className="px-3 py-3">
                        <FilterDropdown
                          label="Worker"
                          col="worker"
                          sort={sort}
                          onSort={toggleSort}
                          activeCount={cols.worker.length}
                          open={openFilterCol === "worker"}
                          onToggle={() =>
                            setOpenFilterCol((p) =>
                              p === "worker" ? null : "worker",
                            )
                          }
                          onClose={() => setOpenFilterCol(null)}
                        >
                          <div className="p-2 max-h-60 overflow-y-auto">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2 pt-1.5 pb-2">
                              Filter by worker
                            </p>
                            <div className="space-y-0.5">
                              {workerOptions.map(({ id, label }) => (
                                <label
                                  key={id}
                                  className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-700/60 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={cols.worker.includes(id)}
                                    onChange={() =>
                                      toggleColValue("worker", id)
                                    }
                                    className="rounded border-gray-500 bg-gray-700 text-orange-500 focus:ring-0 shrink-0"
                                  />
                                  <span className="text-xs text-gray-200 truncate flex-1">
                                    {label}
                                  </span>
                                  <span className="text-[10px] text-gray-600 shrink-0">
                                    {
                                      scopedRequests.filter(
                                        (r) =>
                                          (r.worker?._id || r.worker?.email) ===
                                          id,
                                      ).length
                                    }
                                  </span>
                                </label>
                              ))}
                              {workerOptions.length === 0 && (
                                <p className="text-xs text-gray-600 px-2 py-2">
                                  No workers
                                </p>
                              )}
                            </div>
                            {cols.worker.length > 0 && (
                              <div className="border-t border-gray-700/60 mt-1.5 pt-1.5 px-2">
                                <button
                                  onClick={() => setCol("worker", [])}
                                  className="text-[11px] text-orange-400 hover:text-orange-300"
                                >
                                  Clear
                                </button>
                              </div>
                            )}
                          </div>
                        </FilterDropdown>
                      </th>

                      {/* Items — sortable */}
                      <th
                        className="px-3 py-3 text-left cursor-pointer hover:text-gray-200 select-none"
                        onClick={() => toggleSort("items")}
                      >
                        <span
                          className={cls(
                            "inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider",
                            sort.col === "items"
                              ? "text-orange-400"
                              : "text-gray-400",
                          )}
                        >
                          Items <SortArrows col="items" />
                        </span>
                      </th>

                      {/* Work Date — sortable */}
                      <th
                        className="px-3 py-3 text-left cursor-pointer hover:text-gray-200 select-none whitespace-nowrap"
                        onClick={() => toggleSort("workDate")}
                      >
                        <span
                          className={cls(
                            "inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider",
                            sort.col === "workDate"
                              ? "text-orange-400"
                              : "text-gray-400",
                          )}
                        >
                          Work Date <SortArrows col="workDate" />
                        </span>
                      </th>

                      {/* Submitted — sortable */}
                      <th
                        className="px-3 py-3 text-left cursor-pointer hover:text-gray-200 select-none whitespace-nowrap"
                        onClick={() => toggleSort("submittedAt")}
                      >
                        <span
                          className={cls(
                            "inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider",
                            sort.col === "submittedAt"
                              ? "text-orange-400"
                              : "text-gray-400",
                          )}
                        >
                          Submitted <SortArrows col="submittedAt" />
                        </span>
                      </th>

                      {/* Module — multi-select */}
                      <th className="px-3 py-3">
                        <FilterDropdown
                          label="Module"
                          col={null}
                          sort={sort}
                          onSort={toggleSort}
                          activeCount={cols.module.length}
                          open={openFilterCol === "module"}
                          onToggle={() =>
                            setOpenFilterCol((p) =>
                              p === "module" ? null : "module",
                            )
                          }
                          onClose={() => setOpenFilterCol(null)}
                          align="right"
                        >
                          <div className="p-2 max-h-60 overflow-y-auto">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2 pt-1.5 pb-2">
                              Filter by module
                            </p>
                            <div className="space-y-0.5">
                              {moduleOptions.map((val) => (
                                <label
                                  key={val}
                                  className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-700/60 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={cols.module.includes(val)}
                                    onChange={() =>
                                      toggleColValue("module", val)
                                    }
                                    className="rounded border-gray-500 bg-gray-700 text-orange-500 focus:ring-0 shrink-0"
                                  />
                                  <span className="text-xs text-purple-300 truncate flex-1">
                                    {val}
                                  </span>
                                  <span className="text-[10px] text-gray-600 shrink-0">
                                    {
                                      scopedRequests.filter(
                                        (r) => r.moduleName === val,
                                      ).length
                                    }
                                  </span>
                                </label>
                              ))}
                              {moduleOptions.length === 0 && (
                                <p className="text-xs text-gray-600 px-2 py-2">
                                  No modules
                                </p>
                              )}
                            </div>
                            {cols.module.length > 0 && (
                              <div className="border-t border-gray-700/60 mt-1.5 pt-1.5 px-2">
                                <button
                                  onClick={() => setCol("module", [])}
                                  className="text-[11px] text-orange-400 hover:text-orange-300"
                                >
                                  Clear
                                </button>
                              </div>
                            )}
                          </div>
                        </FilterDropdown>
                      </th>

                      {/* Notes — text search */}
                      <th className="px-3 py-3">
                        <FilterDropdown
                          label="Notes & tags"
                          col={null}
                          sort={sort}
                          onSort={toggleSort}
                          activeCount={(cols.notes ? 1 : 0) + cols.tags.length}
                          open={openFilterCol === "notes"}
                          onToggle={() =>
                            setOpenFilterCol((p) =>
                              p === "notes" ? null : "notes",
                            )
                          }
                          onClose={() => setOpenFilterCol(null)}
                          align="right"
                        >
                          <div className="p-3 space-y-3 max-h-80 overflow-y-auto">
                            <div className="space-y-2">
                              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                Search notes, units, tags
                              </p>
                              <div className="relative">
                                <input
                                  autoFocus
                                  value={cols.notes}
                                  onChange={(e) =>
                                    setCol("notes", e.target.value)
                                  }
                                  placeholder="Unit 6008, rework…"
                                  className="w-full pl-2.5 pr-7 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                                />
                                {cols.notes && (
                                  <button
                                    onClick={() => setCol("notes", "")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-200 text-sm leading-none"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>
                            {tagOptions.length > 0 && (
                              <div className="space-y-1.5">
                                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                  Tags
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {tagOptions.map(({ tag, count }) => {
                                    const on = cols.tags.includes(tag);
                                    return (
                                      <button
                                        key={tag}
                                        onClick={() =>
                                          toggleColValue("tags", tag)
                                        }
                                        className={cls(
                                          "px-2 py-0.5 text-[11px] rounded-full border transition-colors",
                                          on
                                            ? "bg-orange-500/20 border-orange-500/50 text-orange-300"
                                            : "bg-gray-700 border-gray-600 text-gray-400 hover:text-gray-200",
                                        )}
                                      >
                                        #{tag}{" "}
                                        <span className="opacity-60">
                                          {count}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                                {cols.tags.length > 0 && (
                                  <button
                                    onClick={() => setCol("tags", [])}
                                    className="text-[11px] text-orange-400 hover:text-orange-300"
                                  >
                                    Clear tags
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </FilterDropdown>
                      </th>

                      {/* Subtotal */}
                      <th
                        className="px-3 py-3 text-right cursor-pointer hover:text-gray-200 select-none whitespace-nowrap"
                        onClick={() => toggleSort("subtotal")}
                      >
                        <span
                          className={cls(
                            "inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider",
                            sort.col === "subtotal"
                              ? "text-orange-400"
                              : "text-gray-400",
                          )}
                        >
                          Subtotal <SortArrows col="subtotal" />
                        </span>
                      </th>

                      {/* Total */}
                      <th
                        className="px-3 py-3 text-right cursor-pointer hover:text-gray-200 select-none"
                        onClick={() => toggleSort("total")}
                      >
                        <span
                          className={cls(
                            "inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider",
                            sort.col === "total"
                              ? "text-orange-400"
                              : "text-gray-400",
                          )}
                        >
                          Total <SortArrows col="total" />
                        </span>
                      </th>

                      {/* Actions */}
                      <th className="px-3 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-700/50">
                    {filtered.map((req, idx) => {
                      const laborItems = (req.lineItems || []).filter(
                        (i) =>
                          i.type !== "material" &&
                          !i.poItemCode &&
                          !!i.itemCode,
                      );
                      const isSubmitted = req.status === "submitted";
                      const isChecked = selected.has(req._id);
                      const subtotal = req.totalSubtotal || 0;
                      const total = req.totalCost || 0;
                      const hasBurden = total - subtotal > 0.005;

                      return (
                        <tr
                          key={req._id}
                          onClick={() => setExpandedRequest(req)}
                          className={cls(
                            idx % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                            isChecked && "bg-orange-900/10",
                            "hover:bg-gray-600/20 transition-colors cursor-pointer",
                          )}
                        >
                          <td
                            className="px-3 py-2.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isSubmitted && (
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() =>
                                  setSelected((p) => {
                                    const n = new Set(p);
                                    n.has(req._id)
                                      ? n.delete(req._id)
                                      : n.add(req._id);
                                    return n;
                                  })
                                }
                                className="rounded border-gray-500 bg-gray-700"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-orange-400 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              {req.requestNumber || "—"}
                              <DuplicateBadge
                                duplicates={req.duplicates}
                                onClick={() => handleDuplicateClick(req)}
                              />
                            </div>
                          </td>

                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span
                              className={cls(
                                "inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full capitalize",
                                STATUS_BADGE[req.status] ||
                                  "bg-gray-700 text-gray-400",
                              )}
                            >
                              {req.status}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 font-mono text-green-400 whitespace-nowrap">
                            {req.promotedLogId?.logNumber || (
                              <span className="text-gray-700">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <p className="text-gray-200 font-medium truncate max-w-[150px]">
                              {req.workOrder?.title || "—"}
                            </p>
                            {req.workOrder?.woNumber && (
                              <p className="text-[10px] text-gray-500 font-mono">
                                {req.workOrder.woNumber}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-gray-300 whitespace-nowrap">
                            {req.worker?.displayName ||
                              req.worker?.email ||
                              "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-400 font-mono">
                            {laborItems.length}
                          </td>
                          <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">
                            {req.workCompletedDate
                              ? format(
                                  parseISO(req.workCompletedDate),
                                  "MMM d, yyyy",
                                )
                              : "—"}
                          </td>
                          <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                            {req.submittedAt
                              ? format(parseISO(req.submittedAt), "MMM d, yyyy")
                              : "—"}
                          </td>
                          <td className="px-3 py-2.5">
                            {req.moduleName ? (
                              <span className="text-[10px] text-purple-400 bg-purple-900/30 border border-purple-700/30 px-1.5 py-0.5 rounded truncate max-w-[100px] block">
                                {req.moduleName}
                              </span>
                            ) : (
                              <span className="text-gray-700">—</span>
                            )}
                          </td>
                          {/* Notes cell */}
                          <td
                            className="px-3 py-2.5 max-w-[240px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {req.notes ? (
                              <p
                                className="text-gray-300 text-[11px] leading-snug truncate cursor-help"
                                title={req.notes}
                              >
                                {req.notes}
                              </p>
                            ) : (
                              <span className="text-gray-700">—</span>
                            )}
                            <TagChips
                              units={req.units}
                              tags={req.tags}
                              activeTags={cols.tags}
                              onTagClick={(t) => toggleColValue("tags", t)}
                              onUnitClick={(u) => setCol("notes", `unit ${u}`)}
                              className="mt-1"
                            />
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-gray-300 whitespace-nowrap">
                            ${fmt(subtotal)}
                            {req.hstRate > 0 && (
                              <span className="block text-[9px] text-gray-600 font-normal">
                                before HST
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono font-semibold text-orange-300 whitespace-nowrap">
                            ${fmt(total)}
                            {req.hstRate > 0 && hasBurden && (
                              <span className="block text-[9px] text-gray-500 font-normal">
                                incl. HST
                              </span>
                            )}
                          </td>
                          <td
                            className="px-3 py-2.5 whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center gap-1">
                              {isManager && isSubmitted && (
                                <>
                                  <button
                                    onClick={() => handleConfirmGuarded(req)}
                                    disabled={acting}
                                    title="Confirm"
                                    className="px-2.5 py-1 text-[11px] font-semibold text-white bg-green-700 rounded hover:bg-green-600 disabled:opacity-40 transition-colors"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={() => setRejectTarget(req._id)}
                                    disabled={acting}
                                    title="Reject"
                                    className="px-2.5 py-1 text-[11px] font-semibold text-white bg-red-700 rounded hover:bg-red-600 disabled:opacity-40 transition-colors"
                                  >
                                    ✕
                                  </button>
                                </>
                              )}
                              {req.status === "rejected" &&
                                req.rejectionReason && (
                                  <span
                                    className="text-[10px] text-red-400 cursor-help"
                                    title={req.rejectionReason}
                                  >
                                    ⓘ
                                  </span>
                                )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot className="bg-gray-900 border-t border-gray-600">
                    <tr>
                      <td
                        colSpan={11}
                        className="px-3 py-2.5 text-xs text-gray-400"
                      >
                        {filtered.length} request
                        {filtered.length !== 1 ? "s" : ""}
                        {selected.size > 0 && ` · ${selected.size} selected`}
                        {hasActiveFilters &&
                          ` · ${requests.length - filtered.length} hidden by filters`}
                      </td>
                      <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-300 font-mono">
                        $
                        {fmt(
                          filtered.reduce(
                            (s, r) => s + (r.totalSubtotal || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right text-sm font-bold text-orange-300 font-mono">
                        $
                        {fmt(
                          filtered.reduce((s, r) => s + (r.totalCost || 0), 0),
                        )}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {expandedRequest && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4"
          onClick={() => {
            if (isEditingInModal.current) return; // don't close while mid-edit
            setExpandedRequest(null);
          }}
        >
          <div
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 bg-gray-900 rounded-t-xl border border-gray-700 border-b-0">
              <span className="text-xs text-gray-400">
                {expandedRequest.requestNumber}
              </span>
              <button
                onClick={() => {
                  isEditingInModal.current = false;
                  setExpandedRequest(null);
                }}
                className="text-gray-500 hover:text-gray-300 text-lg leading-none px-1"
              >
                ×
              </button>
            </div>
            <EditableRequestCard
              key={expandedRequest._id}
              request={expandedRequest}
              token={token}
              isManager={isManager}
              initialExpanded={true}
              onEditModeChange={(editing) => {
                isEditingInModal.current = editing;
              }}
              onConfirm={async (id) => {
                await handleConfirm(id);
                setExpandedRequest(null);
              }}
              onReject={(id) => {
                setExpandedRequest(null);
                setRejectTarget(id);
              }}
              acting={acting}
              onUpdated={(updated) => {
                handleUpdated(updated);
                setExpandedRequest(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
